<template>
  <div class="legal-page">
    <div class="container py-4 py-lg-5">
      <div
        class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4"
      >
        <div>
          <router-link to="/" class="legal-back">
            <i class="ti ti-arrow-left me-2"></i>Back to site
          </router-link>
          <div class="mt-3">
            <hr-payroll-mark variant="full" />
          </div>
        </div>
        <div class="text-lg-end">
          <span class="badge bg-soft-primary text-primary px-3 py-2 rounded-pill">
            Terms & Conditions
          </span>
          <div class="text-muted small mt-2">
            Last updated: 20 May 2026
          </div>
        </div>
      </div>

      <div class="card legal-card border-0 shadow-sm">
        <div class="card-body p-4 p-lg-5">
          <div class="mb-4">
            <h1 class="fw-bold mb-3">DoneQuick HR & Payroll Terms & Conditions</h1>
            <p class="text-muted mb-0">
              These Terms & Conditions govern access to and use of DoneQuick HR &
              Payroll by a company, its administrators, employees, contractors, and
              other authorized users. By creating an account, starting a trial,
              selecting a plan, or using the platform, you agree to these terms.
            </p>
          </div>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">1. The service covered by these terms</h5>
            <p class="text-muted mb-2">
              DoneQuick HR & Payroll is a business software platform that may include
              HR management, employee records, leave, attendance, timesheets, payroll,
              reporting, loans and savings, finance, projects, CRM, and other related
              modules depending on the plan assigned to your company.
            </p>
            <p class="text-muted mb-0">
              Access to modules, features, and limits depends on the active plan,
              subscription status, trial status, annual licence status, and any
              company-specific configuration.
            </p>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">2. Account ownership and company responsibility</h5>
            <p class="text-muted mb-2">
              The company registering for the service is responsible for all activity
              carried out under its workspace, including actions performed by its
              administrators, managers, employees, invited users, and anyone given
              access credentials.
            </p>
            <ul class="legal-list text-muted mb-0">
              <li>Keep login credentials secure and confidential.</li>
              <li>Assign access rights only to authorized people.</li>
              <li>Ensure data entered into the system is lawful, accurate, and current.</li>
              <li>Promptly disable access for users who should no longer use the platform.</li>
            </ul>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">3. Trials, plans, annual licence fees, and renewal</h5>
            <p class="text-muted mb-2">
              DoneQuick may offer a free trial for eligible companies. Trial access is
              temporary and subject to the duration configured for the assigned plan.
              At the end of a trial, continued use may require payment of the applicable
              plan price and later payment of the annual licence fee under the commercial
              model configured for the company.
            </p>
            <ul class="legal-list text-muted mb-0">
              <li>Plan prices and annual licence fees may differ and are not the same charge.</li>
              <li>Enterprise plans may require direct engagement with sales rather than self-service activation.</li>
              <li>Grace periods, where configured, allow limited time to renew before access is restricted.</li>
              <li>If renewal is not completed by the end of the grace period, system access may be locked while billing remains accessible.</li>
            </ul>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">4. Acceptable use</h5>
            <p class="text-muted mb-2">You agree not to misuse the platform. In particular, you must not:</p>
            <ul class="legal-list text-muted mb-0">
              <li>Use the platform for unlawful, fraudulent, abusive, or deceptive purposes.</li>
              <li>Attempt to gain unauthorized access to another company’s data or workspace.</li>
              <li>Upload malware, harmful code, or content intended to disrupt service integrity.</li>
              <li>Reverse engineer, copy, resell, or exploit the software except as expressly allowed by law or written agreement.</li>
              <li>Use the platform in a way that breaches employment, privacy, tax, payroll, or regulatory obligations applicable to your business.</li>
            </ul>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">5. Customer data and platform records</h5>
            <p class="text-muted mb-2">
              Your company retains responsibility for the data it uploads or generates in
              DoneQuick, including employee records, payroll information, attendance
              records, statutory values, communications, uploaded files, and audit data.
            </p>
            <p class="text-muted mb-0">
              DoneQuick may process that data only as necessary to provide, secure,
              maintain, support, and improve the service, subject to the Privacy Policy
              and any applicable commercial agreement.
            </p>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">6. Compliance, payroll, and reporting</h5>
            <p class="text-muted mb-0">
              The platform may assist with payroll calculations, statutory outputs,
              reminders, reports, and operational workflows. Your company remains
              responsible for reviewing results, confirming payroll accuracy, validating
              compliance positions, and ensuring all filings, deductions, and employee
              payments are correct before use or submission.
            </p>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">7. Service availability and support</h5>
            <p class="text-muted mb-2">
              DoneQuick aims to keep the service available and secure, but uninterrupted
              availability cannot be guaranteed. Maintenance, upgrades, third-party
              outages, internet failures, or security events may affect access from time
              to time.
            </p>
            <p class="text-muted mb-0">
              Any free support period or onboarding support available under a plan is
              limited to the scope and duration configured for that plan or agreed in
              writing.
            </p>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">8. Suspension, restriction, and termination</h5>
            <p class="text-muted mb-2">
              Access may be suspended, limited, or terminated where reasonably necessary,
              including for non-payment, expired renewal periods, suspected security
              issues, misuse of the platform, or breach of these terms.
            </p>
            <p class="text-muted mb-0">
              Where possible, billing and renewal pages may remain accessible so the
              company can regularize its account.
            </p>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">9. Intellectual property</h5>
            <p class="text-muted mb-0">
              DoneQuick and its related branding, software, designs, workflows, and
              platform materials remain the property of their respective owners and
              licensors. These terms do not transfer ownership of the software to the
              customer. Your company receives only the right to use the service in
              accordance with its active plan and these terms.
            </p>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">10. Disclaimers</h5>
            <p class="text-muted mb-0">
              The service is provided on an “as available” basis. Except where the law
              does not allow such exclusions, DoneQuick does not guarantee that the
              platform will always be error-free, uninterrupted, or suitable for every
              business use case. Your company should maintain appropriate internal review,
              approval, backup, and compliance checks.
            </p>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">11. Limitation of liability</h5>
            <p class="text-muted mb-0">
              To the maximum extent permitted by law, DoneQuick will not be liable for
              indirect, incidental, special, punitive, or consequential losses, including
              loss of profit, data, business opportunity, or goodwill arising from use of
              or inability to use the service. Any direct liability is limited to the
              amount paid by the customer for the relevant service period giving rise to
              the claim, unless a different limit is stated in a written commercial
              agreement.
            </p>
          </section>

          <section class="mb-4">
            <h5 class="fw-bold mb-2">12. Changes to these terms</h5>
            <p class="text-muted mb-0">
              DoneQuick may update these terms from time to time. The latest version will
              be posted on this page. Continued use of the platform after updates take
              effect means the company accepts the revised terms.
            </p>
          </section>

          <section>
            <h5 class="fw-bold mb-2">13. Questions</h5>
            <p class="text-muted mb-0">
              If your company has questions about these terms, contact your account owner,
              account manager, or official DoneQuick support channel before continuing to
              use the service.
            </p>
          </section>
        </div>
      </div>

      <div class="d-flex flex-wrap gap-3 justify-content-between align-items-center mt-4">
        <p class="mb-0 text-muted small">
          2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll
        </p>
        <div class="d-flex gap-2 flex-wrap">
          <router-link to="/privacy-policy" class="btn btn-outline-secondary btn-sm">
            Privacy Policy
          </router-link>
          <router-link to="/register" class="btn btn-primary btn-sm">
            Back to signup
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HrPayrollMark from "@/components/branding/hr-payroll-mark.vue";

export default {
  components: {
    HrPayrollMark,
  },
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background:
    radial-gradient(900px 420px at 0% 0%, rgba(255, 103, 31, 0.14), transparent 60%),
    linear-gradient(180deg, #fffdfb 0%, #f8fafc 100%);
}

.legal-card {
  border-radius: 24px;
}

.legal-back {
  color: #0f172a;
  font-weight: 600;
  text-decoration: none;
}

.legal-back:hover {
  color: #ff671f;
}

.bg-soft-primary {
  background: rgba(255, 103, 31, 0.12);
}

.legal-list {
  padding-left: 1.2rem;
}

.legal-list li + li {
  margin-top: 0.5rem;
}
</style>
